import { capitalize } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import Button from '@atlaskit/button';
import { Field } from '@atlaskit/form';
import Select, { ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import {
  BillingFrequency,
  EntitlementDetails,
  PricingPlan,
  PricingTiers as PricingTiersType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getDefaultPricingPlan, notActivePricingStatus } from '@atlassian/cssxp-utils-entitlement';
import { useApprovalDeskPermissions } from '@css-xp/apd-common';
import { Icon, SummaryGridItem, SummarySection } from '@css-xp/base';
import { PricingTiers } from '@css-xp/base/common-tabs';
import { FormData } from '../../../sidebar-panel/manage-entitlement/src/manage-entitlement';
import './change-price-plan.scss';

interface Props {
  entitlement: EntitlementDetails;
  formState?: FormData;
  isManagePane?: boolean;
  availablePricingPlans?: PricingPlan[];
  planId?: string;
  viewTierDetails?: boolean;
  selectedOfferingId?: string;
  billingFrequency?: string | null | undefined;
  setSelectedPricingPlan?: (pricingPlan: PricingPlan) => void;
  shouldResetPricingPlan?: { current: boolean };
}
interface PricingSelectItem {
  label: string;
  value: string;
  chargeElement: string;
  chargeType: string;
  pricingPlanType: string;
  description: string;
  status: string;
}

const getPricingPlanDescription = (pricingPlan: PricingPlan) => {
  if (pricingPlan) {
    return (
      pricingPlan?.description +
      ' | ' +
      capitalize(pricingPlan?.pricingPlanType || '').replace(/_/g, ' ') +
      ' | ' +
      capitalize(pricingPlan?.status || '')
    );
  }
  return 'Not Applicable';
};

export const ChangePricePlan = ({
  entitlement,
  isManagePane = true,
  availablePricingPlans = [],
  planId = '',
  formState,
  viewTierDetails = true,
  selectedOfferingId,
  billingFrequency,
  setSelectedPricingPlan = () => null,
  shouldResetPricingPlan,
}: Props) => {
  const [showSummary, setIsSummary] = React.useState<boolean>(formState?.pricePlan ? true : false);
  const [showPricingPlanList, setShowPricingPlanList] = useState<boolean>(formState?.pricePlan ? true : false);
  const [pricingPlanId, setPricingPlanId] = useState<string | undefined>(planId);
  const formStateUsed = React.useRef(false);

  const filteredAvailablePricingPlans = useMemo(() => {
    return availablePricingPlans.filter((pricingPlan) => {
      if (billingFrequency === BillingFrequency.MONTHLY) {
        return (
          !notActivePricingStatus.includes(pricingPlan?.status as string) &&
          pricingPlan?.billingFrequency?.toUpperCase() === BillingFrequency.MONTHLY
        );
      }
      if (billingFrequency === BillingFrequency.ANNUAL) {
        return (
          !notActivePricingStatus.includes(pricingPlan?.status as string) &&
          pricingPlan?.billingFrequency?.toUpperCase() === BillingFrequency.ANNUAL
        );
      }
      return !notActivePricingStatus.includes(pricingPlan?.status as string);
    });
  }, [availablePricingPlans, billingFrequency]);
  const availablePricingPlanOptions: PricingSelectItem[] = useMemo(() => {
    return filteredAvailablePricingPlans.map((pricing: PricingPlan) => ({
      label: getPricingPlanDescription(pricing),
      value: pricing.id,
      chargeElement: pricing?.chargeElement ?? '',
      chargeType: pricing?.chargeType ?? '',
      pricingPlanType: pricing?.pricingPlanType ?? '',
      description: pricing?.description ?? '',
      status: pricing?.status ?? '',
    }));
  }, [filteredAvailablePricingPlans]);

  const { pricingPlan, activeUsers } = entitlement;

  const defaultPricingPlanOption = useMemo(() => {
    const defaultPricingPlan = getDefaultPricingPlan(filteredAvailablePricingPlans);
    if (defaultPricingPlan) {
      return {
        label: getPricingPlanDescription(defaultPricingPlan),
        value: defaultPricingPlan.id,
        chargeElement: defaultPricingPlan?.chargeElement ?? '',
        chargeType: defaultPricingPlan?.chargeType ?? '',
        pricingPlanType: defaultPricingPlan?.pricingPlanType ?? '',
        description: defaultPricingPlan?.description ?? '',
        status: defaultPricingPlan?.status ?? '',
      };
    }
    return undefined;
  }, [filteredAvailablePricingPlans]);

  const selectedPricingPlan = useMemo(() => {
    const pricingPlan: PricingPlan | undefined = filteredAvailablePricingPlans.find(
      (pricingPlan) => pricingPlan?.id === pricingPlanId
    );
    setSelectedPricingPlan(pricingPlan as PricingPlan);
    return pricingPlan;
  }, [
    pricingPlanId,
    filteredAvailablePricingPlans,
    billingFrequency,
    availablePricingPlans,
    setSelectedPricingPlan,
    showPricingPlanList,
    selectedOfferingId,
  ]);
  const nameField =
    pricingPlanId === entitlement?.pricingPlan?.id
      ? getPricingPlanDescription(entitlement?.pricingPlan as PricingPlan)
      : getPricingPlanDescription((selectedPricingPlan as unknown) as PricingPlan);
  const availablePricingPlanTiers = selectedPricingPlan?.tiers;
  const selectedPricingBillingFrequency = selectedPricingPlan?.billingFrequency;
  useEffect(() => {
    if (isManagePane && shouldResetPricingPlan?.current && defaultPricingPlanOption?.value) {
      setPricingPlanId(defaultPricingPlanOption?.value);
      shouldResetPricingPlan.current = false;
    } else if (isManagePane && formState?.pricePlan?.value && !formStateUsed.current) {
      /* formStateUsed ref is being used to make sure that formState values are used only once when we land on the 
        manage section,for subsequent changes default value will be used to set pricing plan this is for the case where user comes back to manage plan after seeing review screen */
      setPricingPlanId(formState?.pricePlan?.value);
      formStateUsed.current = true;
    } else if (formState?.pricePlan?.value && !isManagePane) {
      setPricingPlanId(formState?.pricePlan?.value);
    } else if (formState?.newPlan?.value && formState?.newPlan?.value !== entitlement.offeringId) {
      setPricingPlanId(defaultPricingPlanOption?.value);
    } else if (selectedOfferingId && selectedOfferingId !== entitlement.offeringId) {
      setPricingPlanId(defaultPricingPlanOption?.value);
    } else if (defaultPricingPlanOption?.value) {
      setPricingPlanId(defaultPricingPlanOption?.value);
    }
  }, [
    entitlement,
    pricingPlan,
    defaultPricingPlanOption,
    selectedOfferingId,
    formState,
    shouldResetPricingPlan,
    isManagePane,
    billingFrequency,
  ]);
  const { isPricingPlanChangeApprovalFlowEnabled } = useApprovalDeskPermissions();
  return (
    <>
      {!showPricingPlanList && (
        <div className="pricingField">
          <Field label={'Price plan name'} name="pricePlanName" defaultValue={nameField}>
            {({ fieldProps }) => (
              <Textfield
                className="pricing-field"
                {...fieldProps}
                name={'pricePlanName'}
                isDisabled
                testId="pricing-field"
              />
            )}
          </Field>
          <Field name="pricePlanId" defaultValue={selectedPricingPlan?.id}>
            {() => null}
          </Field>
        </div>
      )}
      {showPricingPlanList && (
        <>
          <Field
            name="pricePlan"
            id="pricePlan"
            label="Price plan name"
            isRequired={false}
            defaultValue={
              selectedPricingPlan
                ? {
                    label: getPricingPlanDescription(selectedPricingPlan as PricingPlan),
                    value: selectedPricingPlan?.id,
                    chargeElement: selectedPricingPlan?.chargeElement ?? '',
                    chargeType: selectedPricingPlan?.chargeType ?? '',
                    pricingPlanType: selectedPricingPlan?.pricingPlanType ?? '',
                    status: selectedPricingPlan?.status || '',
                    description: selectedPricingPlan?.description || '',
                  }
                : undefined
            }
            isDisabled={!isManagePane}
          >
            {({ fieldProps }) => (
              <Select<PricingSelectItem>
                {...fieldProps}
                value={
                  selectedPricingPlan
                    ? {
                        label: getPricingPlanDescription(selectedPricingPlan as PricingPlan),
                        value: selectedPricingPlan?.id,
                        chargeElement: selectedPricingPlan?.chargeElement ?? '',
                        chargeType: selectedPricingPlan?.chargeType ?? '',
                        pricingPlanType: selectedPricingPlan?.pricingPlanType ?? '',
                        status: selectedPricingPlan?.status || '',
                        description: selectedPricingPlan?.description || '',
                      }
                    : null
                }
                isSearchable={false}
                options={availablePricingPlanOptions}
                data-testid="change-pricing-plan"
                placeholder={'Select pricing plan'}
                onChange={(pricing: ValueType<PricingSelectItem, false>) => {
                  setPricingPlanId(pricing?.value);
                  setShowPricingPlanList(true);
                  setIsSummary(true);
                  fieldProps.onChange(pricing as PricingSelectItem);
                }}
              ></Select>
            )}
          </Field>
          {isPricingPlanChangeApprovalFlowEnabled && (
            <div className="approval-info-message">
              <Icon type="warning" />
              <span>Some pricing plan changes require approval</span>
            </div>
          )}
        </>
      )}

      {showSummary && selectedPricingPlan && (
        <>
          <div>
            <SummarySection className="summary-section-internal pricing-summary" columns="1fr 1fr 1fr">
              <SummaryGridItem className="section-item-internal pricing-details">
                <div>
                  <label>Plan</label>
                  <div>{selectedPricingPlan?.offeringName}</div>
                </div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal pricing-details">
                <div>
                  <label>Status</label>
                  <div>{capitalize(selectedPricingPlan?.status || '')}</div>
                </div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal pricing-details">
                <div>
                  <label>Pricing plan</label>
                  <div>{capitalize(selectedPricingPlan?.pricingPlanType || '').replace(/_/g, ' ')}</div>
                </div>
              </SummaryGridItem>
            </SummarySection>
            <SummarySection className="summary-section-internal pricing-summary" columns="1fr 1fr 1fr">
              <SummaryGridItem className="section-item-internal pricing-details">
                <div>
                  <label>Billing cycle</label>
                  <div>{capitalize(selectedPricingPlan?.billingFrequency || '')}</div>
                </div>
              </SummaryGridItem>
              <SummaryGridItem className="section-item-internal pricing-details">
                <div>
                  <label>Number of users</label>
                  <div>{activeUsers ? activeUsers : '-'}</div>
                </div>
              </SummaryGridItem>
            </SummarySection>
          </div>

          {isManagePane && (
            <PricingTiers
              pricingPlan={pricingPlan}
              activeUsers={activeUsers}
              availablePricingPlanTiers={availablePricingPlanTiers as PricingTiersType[]}
              selectedPricingBillingFrequency={selectedPricingBillingFrequency as BillingFrequency}
              viewTierDetails={viewTierDetails}
            />
          )}
        </>
      )}

      {!showPricingPlanList && isManagePane && (
        <div className="change-price-plan">
          <Button
            aria-label="Change pricing plan"
            appearance="link"
            className="inner-text"
            onClick={() => setShowPricingPlanList(true)}
            testId="change-price-plan-btn"
          >
            Change pricing plan
          </Button>
        </div>
      )}
      <div className="pricing-plan-summary"></div>
    </>
  );
};
